import React, { ReactElement, useState } from 'react'
import cardArtGenericMastercard from 'src/assets/svgs/card-art-generic-mastercard.svg'
import { useTranslate } from 'src/localization/Trns'

import { PhysicalCardManagementStatus } from '../cardDetailHeroDemoData'
import SvgLock from '../icons/SvgLock'
import SvgSnowflake from '../icons/SvgSnowflake'
import { useCardStatusLabel } from '../useCardStatusLabel'
import * as style from './CardArt.style'

type CardArtProps = {
  /**
   * `undefined` when the backend status has no card-management counterpart. The
   * accessible name then omits the status clause rather than claiming one, and
   * neither overlay renders.
   */
  status: PhysicalCardManagementStatus | undefined
  network: string
  last4: string
  holder: string
  /** Issuer artwork and its API-provided credential contrast styling. */
  imageUrl: string
  textColor: string
  textShadow: string
  /** Full PAN — rendered only when `revealed`; masked to the last four otherwise. */
  pan: string
  exp: string
  revealed?: boolean
}

const MASKED_PREFIX = '••••  ••••  ••••  '
const MASKED_EXP = '••/••'

/** MASTERCARD → Mastercard, for the accessible name (a brand, not a translation). */
const networkLabel = (network: string): string =>
  network.charAt(0).toUpperCase() + network.slice(1).toLowerCase()

/**
 * The physical card face. `role="img"` with a status-bearing accessible name, so
 * the whole card reads as one image and the on-face credentials are not
 * announced field-by-field. Frozen/locked are conveyed by a visible overlay
 * label — never colour alone — and agree with the summary pill via the shared
 * status the shell owns.
 */
const CardArt: React.FC<CardArtProps> = ({
  status,
  network,
  last4,
  holder,
  imageUrl,
  textColor,
  textShadow,
  pan,
  exp,
  revealed = false,
}): ReactElement => {
  const translate = useTranslate()
  const statusLabel = useCardStatusLabel()
  const [failedImageUrl, setFailedImageUrl] = useState<string | undefined>()
  const faceSrc =
    failedImageUrl === imageUrl ? cardArtGenericMastercard : imageUrl
  const credentialStyle = { color: textColor, textShadow }

  return (
    <div
      css={style.card}
      role="img"
      aria-label={
        status !== undefined
          ? translate(
              '${cardNetwork} physical card ending ${last4}, ${status}',
              {
                cardNetwork: networkLabel(network),
                last4,
                status: statusLabel(status),
              },
            )
          : translate('${cardNetwork} physical card ending ${last4}', {
              cardNetwork: networkLabel(network),
              last4,
            })
      }
      data-testid="card-art"
    >
      <img
        src={faceSrc}
        alt=""
        css={style.face}
        data-testid="card-art-face"
        onError={() => {
          // Preserve a usable card face if an issuer asset is temporarily
          // unavailable. Remember the failed URL rather than a boolean so a
          // subsequent card/image prop can try its own artwork immediately.
          if (faceSrc === imageUrl) setFailedImageUrl(imageUrl)
        }}
      />
      <div css={style.credentials}>
        <span
          css={style.pan}
          style={credentialStyle}
          data-testid="card-art-pan"
        >
          {revealed ? pan : MASKED_PREFIX + last4}
        </span>
        <span
          css={style.exp}
          style={credentialStyle}
          data-testid="card-art-exp"
        >
          {revealed ? exp : MASKED_EXP}
        </span>
        <span css={style.holder} style={credentialStyle}>
          {holder}
        </span>
      </div>
      {status === 'FROZEN' && (
        <div css={style.overlayFrozen} data-testid="card-art-overlay-frozen">
          <SvgSnowflake width={30} height={30} />
          <span>{statusLabel('FROZEN')}</span>
        </div>
      )}
      {status === 'LOCKED' && (
        <div css={style.overlayLocked} data-testid="card-art-overlay-locked">
          <SvgLock width={28} height={28} />
          <span>{translate('Locked by Admin')}</span>
        </div>
      )}
    </div>
  )
}

export default CardArt
